import { useEffect, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { X } from 'lucide-react';
import WeeklyScheduleEditor from '../../../common/addressSchedule/WeeklyScheduleEditor.tsx';
import { validateWeeklySchedule } from '../../../common/addressSchedule/scheduleUtils.ts';
import { getLinkPageScheduleCopy } from '../services/api';
import { hasRegisteredOpeningHours } from '../pages/admin/linksConfig.utils';

const DAYS = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
const emptySchedule = () => ({ timezone: 'America/Sao_Paulo', exceptions: [],
  days: DAYS.map((label, index) => ({ day: String(index), label, closed: true, periods: [] })) });
export default function LinksConfigSchedulePanel({ restaurant, setRestaurant, copyOptions = [] }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [copying, setCopying] = useState('');
  const [error, setError] = useState('');
  const requestVersion = useRef(0);
  useEffect(() => () => { requestVersion.current += 1; }, []);
  const schedule = draft || emptySchedule();
  const update = transform => setDraft(current => transform(current || emptySchedule()));
  const updateDay = (index, transform) => update(current => ({ ...current,
    days: current.days.map((day, dayIndex) => dayIndex === index ? transform(day) : day) }));
  const openEditor = () => {
    requestVersion.current += 1;
    setDraft(restaurant.openingHours ? JSON.parse(JSON.stringify(restaurant.openingHours)) : null);
    setError('');
    setCopying('');
    setOpen(true);
  };
  const closeEditor = () => {
    requestVersion.current += 1;
    setCopying('');
    setOpen(false);
  };
  const copy = async source => {
    const version = ++requestVersion.current;
    setCopying(source);
    setError('');
    try {
      const result = await getLinkPageScheduleCopy(source);
      if (version === requestVersion.current) setDraft(result);
    } catch (failure) {
      if (version === requestVersion.current) setError(failure?.response?.data?.error || 'Não foi possível copiar os horários agora.');
    } finally { if (version === requestVersion.current) setCopying(''); }
  };
  const issue = draft ? validateWeeklySchedule(schedule.days).issues.find(item => item.key !== 'all_closed')?.message : '';
  const apply = () => {
    if (copying || issue) return;
    setRestaurant(current => ({ ...current, openingHours: draft,
      showHours: hasRegisteredOpeningHours(draft) && current.showHours !== false }));
    closeEditor();
  };

  return (
    <>
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="fsl-field-label">Horários da LinkPage</h3>
          <p className="fsl-field-hint">{restaurant.openingHours ? 'Horários cadastrados. Edite ou copie de outro produto.' : 'Cadastre os horários para habilitar o resumo e o indicador aberto/fechado.'}</p>
        </div>
        <Button variant="outlined" onClick={openEditor}>Configurar horários</Button>
      </div>
      <Dialog open={open} onClose={closeEditor} fullWidth maxWidth="md" scroll="paper"
        aria-labelledby="linkpage-schedule-title" PaperProps={{ className: 'fsl-schedule-dialog', sx: { borderRadius: 3, maxHeight: 'calc(100dvh - 32px)', m: 2, width: 'calc(100% - 32px)' } }}>
        <DialogTitle id="linkpage-schedule-title" sx={{ pr: 7 }}>Horários da LinkPage</DialogTitle>
        <IconButton aria-label="Fechar horários" onClick={closeEditor} sx={{ position: 'absolute', top: 12, right: 12 }}><X size={20} /></IconButton>
        <DialogContent dividers>
        <div className="min-w-0 space-y-3">
      <p className="text-sm text-slate-600">Cadastre os horários ou copie do AtendaBot ou Reservo. Revise e aplique para atualizar o formulário; depois salve as alterações da página.</p>
      <div className="flex flex-wrap gap-2">
        {copyOptions.filter(source => ['atendabot', 'reservo'].includes(source.id)).map(source => (
          <button key={source.id} type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold disabled:opacity-60" disabled={Boolean(copying)} onClick={() => copy(source.id)}>
            {copying === source.id ? 'Copiando…' : `Copiar do ${source.label}`}
          </button>
        ))}
        {draft && <button type="button" disabled={Boolean(copying)} className="px-3 py-2 text-sm text-slate-600"
          onClick={() => setDraft(null)}>Limpar horários</button>}
      </div>
      {error && <p role="alert" className="fsl-upload-error">{error}</p>}
      <WeeklyScheduleEditor days={schedule.days} inputClassName="fsl-input" error={issue} maxPeriodsPerDay={6} disabled={Boolean(copying)}
        onToggleDayClosed={(index, closed) => updateDay(index, day => ({ ...day, closed, periods: closed ? [] : [{ start: '09:00', end: '18:00' }] }))}
        onAddPeriod={index => updateDay(index, day => ({ ...day, periods: [...day.periods, { start: '', end: '' }] }))}
        onUpdatePeriod={(index, periodIndex, field, value) => updateDay(index, day => ({ ...day,
          periods: day.periods.map((period, position) => position === periodIndex ? { ...period, [field]: value } : period) }))}
        onRemovePeriod={(index, periodIndex) => updateDay(index, day => {
          const periods = day.periods.filter((_, position) => position !== periodIndex);
          return { ...day, periods, closed: !periods.length };
        })}
        onReplicateAll={period => update(current => ({ ...current, days: current.days.map(day => ({ ...day, closed: false, periods: [{ ...period }] })) }))}
      />
      {schedule.exceptions?.length > 0 && <div className="space-y-2 text-sm">
        <strong>Exceções por data copiadas</strong>
        {schedule.exceptions.map(exception => <div key={exception.date} className="flex flex-wrap items-center justify-between gap-2">
          <span>{exception.date.split('-').reverse().join('/')}: {exception.closed ? 'Fechado' : exception.periods.map(period => `${period.start} às ${period.end}`).join(', ')}</span>
          <button type="button" disabled={Boolean(copying)} onClick={() => update(current => ({ ...current, exceptions: current.exceptions.filter(item => item.date !== exception.date) }))}
            aria-label={`Remover exceção de ${exception.date}`}>Remover</button>
        </div>)}
      </div>}
        </div>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeEditor}>Cancelar</Button>
          <Button variant="contained" onClick={apply} disabled={Boolean(copying || issue)}>Aplicar horários</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
